export type SocialPrefill = {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  /**
   * If provided, we may try to decode it (JWT id_token)
   * to extract additional profile fields.
   */
  idToken?: string;
  provider?: "google" | "unknown";
};

const STORAGE_KEY = "pendingSocialPrefill";

function safeJsonParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function base64UrlDecodeToString(input: string) {
  const base64 = input.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "===".slice((base64.length + 3) % 4);
  // atob is available in browsers; keep runtime simple for CRA.
  return atob(padded);
}

export function decodeJwtPayload(token?: string): Record<string, any> | null {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  try {
    const json = base64UrlDecodeToString(parts[1]);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function normalizePhone10(phone?: string) {
  if (!phone) return undefined;
  const digits = phone.replace(/\D/g, "");
  // Keep last 10 digits (handles +91XXXXXXXXXX cases)
  const last10 = digits.length >= 10 ? digits.slice(-10) : digits;
  return last10 || undefined;
}

/**
 * Build a normalized prefill object from a provider token/payload.
 * Note: phone is rarely present in Google/Apple tokens; we keep it optional.
 */
export function normalizeSocialPrefill(input: SocialPrefill): SocialPrefill {
  const out: SocialPrefill = { ...input };

  if (out.phoneNumber) out.phoneNumber = normalizePhone10(out.phoneNumber);

  const claims = decodeJwtPayload(out.idToken);
  if (claims) {
    // Common OIDC claims
    out.email = out.email || claims.email;

    const name = typeof claims.name === "string" ? claims.name : undefined;
    const given = typeof claims.given_name === "string" ? claims.given_name : undefined;
    const family = typeof claims.family_name === "string" ? claims.family_name : undefined;

    if (!out.firstName && given) out.firstName = given;
    if (!out.lastName && family) out.lastName = family;

    if ((!out.firstName || !out.lastName) && name) {
      const parts = name.trim().split(/\s+/).filter(Boolean);
      if (!out.firstName && parts.length) out.firstName = parts[0];
      if (!out.lastName && parts.length >= 2) out.lastName = parts[parts.length - 1];
    }

    // Rare, but supported by some IdPs
    const phoneClaim =
      (typeof claims.phone_number === "string" && claims.phone_number) ||
      (typeof claims.phoneNumber === "string" && claims.phoneNumber) ||
      undefined;
    if (!out.phoneNumber && phoneClaim) out.phoneNumber = normalizePhone10(phoneClaim);
  }

  return out;
}

export function setPendingSocialPrefill(prefill: SocialPrefill) {
  const normalized = normalizeSocialPrefill(prefill);
  // Debug: inspect what we received/stored (safe to remove later)
  console.log("[socialPrefill] setPendingSocialPrefill input:", prefill);
  console.log("[socialPrefill] normalized:", normalized);
  if (normalized.idToken) {
    console.log("[socialPrefill] decoded idToken claims:", decodeJwtPayload(normalized.idToken));
  }
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
}

export function getPendingSocialPrefill(): SocialPrefill | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  const parsed = safeJsonParse<SocialPrefill>(raw);
  const normalized = parsed ? normalizeSocialPrefill(parsed) : null;
  if (normalized) {
    // Debug: inspect what register page is about to consume (safe to remove later)
    console.log("[socialPrefill] getPendingSocialPrefill:", normalized);
  }
  return normalized;
}

export function clearPendingSocialPrefill() {
  sessionStorage.removeItem(STORAGE_KEY);
}

